import { useCallback, useEffect, useMemo, useState } from 'react';
import { useScope } from '@workspace/scope-context';
import useSWR from 'swr';

import { Badge, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@workspace/ui';

import {
  composeRuntimeSurfaceSectionRenderers,
  createRuntimeSurfaceComponentRenderer,
} from '@/components/mapping/runtimeSurfaceRenderers';
import {
  SALES_MAP_DETAILS_COMPONENT_ID,
  renderSalesMapDetailsSection,
} from '@/components/sales/SalesMapDetailsSection';
import {
  SALES_MAP_COLLABORATION_COMPONENT_ID,
  renderSalesMapCollaborationSection,
} from '@/components/sales/SalesMapCollaborationSection';
import {
  SALES_MAP_ORDERS_COMPONENT_ID,
  renderSalesMapOrdersSection,
} from '@/components/sales/SalesMapOrdersSection';
import {
  SALES_MAP_ROUTING_QUOTE_COMPONENT_ID,
  SalesMapRoutingProvider,
  renderSalesMapRoutingQuoteSection,
} from '@/components/sales/SalesMapRoutingQuoteSection';
import {
  SalesMapServiceabilityZoneHoverCard,
  SalesMapServiceabilityZonePanel,
} from '@/components/sales/SalesMapServiceabilityZoneSurface';
import { MappingFeatureUnavailable, MappingPermissionDenied } from '@/components/mapping/MappingFeatureUnavailable';
import { getMapStyle, listMapStyles, type MapStyle } from '@/lib/mapping-api';
import { listLinkedSellers } from '@/lib/partner-links';
import { listResellerEntitlements } from '@/lib/commerce';
import {
  buildSalesMapContextOptions,
  selectDefaultSalesMapContext,
  toSalesWorkspaceContext,
  type SalesMapContextOption,
} from '@/lib/sales-map-context';
import { hydrateSalesMapRuntimeFeatureState } from '@/lib/sales-map-runtime-feature-state';
import {
  buildSalesMapZoneOverlayGeoJSON,
  fetchSalesServiceabilityZones,
  mergeRuntimeOverlayGeoJSON,
  readSalesServiceabilityZoneSelection,
  type SalesServiceabilityZoneOverlaySelection,
} from '@/lib/sales-zones';
import { useMappingAccess } from '@/lib/useMappingAccess';
import { useTenantFeatureMap } from '@/lib/useTenantFeatureMap';
import MapStyleWorkbenchPage from './MapStyleWorkbenchPage';

const SALES_MAP_SYSTEM_KEY = 'sales-map-default';
const SALES_MAP_SYSTEM_NAME = 'Sales Map Default';
const PORTAL_DEBUG_LOGS_ENABLED = import.meta.env.DEV && import.meta.env.MODE !== 'test';

type SalesMapStyleRecord = MapStyle & {
  style_class?: string | null;
  system_key?: string | null;
};

const normalize = (value: string | null | undefined) => value?.trim().toLowerCase() ?? '';

const isSalesMapDefaultStyle = (style: SalesMapStyleRecord) => {
  const systemKey = normalize(style.system_key);
  if (systemKey === SALES_MAP_SYSTEM_KEY) {
    return true;
  }
  return normalize(style.name) === normalize(SALES_MAP_SYSTEM_NAME);
};

const logSalesMapPageDebug = (message: string, details?: Record<string, unknown>) => {
  if (!PORTAL_DEBUG_LOGS_ENABLED || typeof console === 'undefined' || typeof console.debug !== 'function') {
    return;
  }
  if (details) {
    console.debug(`[SalesMap][Page] ${message}`, details);
    return;
  }
  console.debug(`[SalesMap][Page] ${message}`);
};

const summarizeRuntimeOverlayGeoJSON = (value: Record<string, unknown> | null) => {
  const features = Array.isArray(value?.features) ? (value.features as Array<Record<string, unknown>>) : [];
  return {
    featureCount: features.length,
    geometryTypes: features.map(feature => {
      const geometry = feature.geometry;
      return geometry && typeof geometry === 'object' && typeof (geometry as { type?: unknown }).type === 'string'
        ? (geometry as { type: string }).type
        : 'unknown';
    }),
  };
};

const SALES_MAP_RUNTIME_OVERLAY_STYLE = {
  fillPaint: {
    'fill-color': [
      'case',
      ['==', ['get', 'overlay_kind'], 'serviceability_zone'],
      ['case', ['boolean', ['get', 'selected'], false], 'rgb(30 64 175)', 'rgb(59 130 246)'],
      'rgba(0, 0, 0, 0)',
    ],
    'fill-opacity': [
      'case',
      ['==', ['get', 'overlay_kind'], 'serviceability_zone'],
      ['case', ['boolean', ['get', 'selected'], false], 0.34, 0.24],
      0,
    ],
  },
  linePaint: {
    'line-color': [
      'case',
      ['==', ['get', 'overlay_kind'], 'serviceability_zone'],
      ['case', ['boolean', ['get', 'selected'], false], 'rgb(30 64 175)', 'rgb(37 99 235)'],
      ['==', ['get', 'role'], 'route'],
      'rgba(249, 115, 22, 0.98)',
      'rgba(14, 165, 233, 0.98)',
    ],
    'line-width': [
      'case',
      ['==', ['get', 'overlay_kind'], 'serviceability_zone'],
      ['interpolate', ['linear'], ['zoom'], 6, 1.25, 10, 1.9, 14, 2.5],
      ['interpolate', ['linear'], ['zoom'], 8, 2, 12, 4, 16, 6],
    ],
    'line-opacity': [
      'case',
      ['==', ['get', 'overlay_kind'], 'serviceability_zone'],
      0.98,
      0.95,
    ],
  },
  pointPaint: {
    'circle-color': [
      'case',
      ['==', ['get', 'role'], 'access_point'],
      'rgba(14, 165, 233, 1)',
      ['==', ['get', 'role'], 'origin'],
      'rgba(249, 115, 22, 1)',
      'rgba(249, 115, 22, 1)',
    ],
    'circle-radius': [
      'case',
      ['==', ['get', 'role'], 'access_point'],
      5,
      6,
    ],
    'circle-stroke-color': 'rgba(255, 255, 255, 0.92)',
    'circle-stroke-width': 2,
  },
} as const;

export function SalesMapPage() {
  const { activeTenant } = useScope();
  const tenantId = activeTenant?.id ?? null;
  const { featureMap } = useTenantFeatureMap();
  const mappingAccess = useMappingAccess();
  const salesAccessEnabled = featureMap?.['sales.runtime.enabled'] !== false;
  const { data: linkedSellers = [] } = useSWR(
    tenantId ? ['linked-sellers', tenantId] : null,
    () => listLinkedSellers(tenantId!, { limit: 200 }).then(page => page.items),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
    },
  );
  const { data: resellerEntitlements = [] } = useSWR(
    tenantId ? ['sales-map-entitlements', tenantId] : null,
    () => listResellerEntitlements({ limit: 200 }).then(page => page.items),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
    },
  );
  const salesMapContextOptions = useMemo(
    () =>
      tenantId
        ? buildSalesMapContextOptions({
            activeTenantId: tenantId,
            activeTenantName: activeTenant?.name ?? null,
            linkedSellers,
            entitlements: resellerEntitlements,
          })
        : [],
    [activeTenant?.name, linkedSellers, resellerEntitlements, tenantId],
  );
  const [selectedSalesMapContextId, setSelectedSalesMapContextId] = useState<string | null>(null);
  const [salesMapContextSelectionTouched, setSalesMapContextSelectionTouched] = useState(false);

  useEffect(() => {
    const preferredOption = selectDefaultSalesMapContext(salesMapContextOptions);
    setSelectedSalesMapContextId(current => {
      const currentOption = current
        ? salesMapContextOptions.find(option => option.id === current) ?? null
        : null;
      if (salesMapContextSelectionTouched && currentOption) {
        return current;
      }
      if (
        currentOption &&
        !(currentOption.kind === 'self' && preferredOption?.kind === 'linked-seller')
      ) {
        return current;
      }
      return preferredOption?.id ?? null;
    });
  }, [salesMapContextOptions, salesMapContextSelectionTouched]);

  const selectedSalesMapContext = useMemo<SalesMapContextOption | null>(
    () => salesMapContextOptions.find(option => option.id === selectedSalesMapContextId) ?? salesMapContextOptions[0] ?? null,
    [salesMapContextOptions, selectedSalesMapContextId],
  );
  const salesWorkspaceContext = useMemo(
    () => toSalesWorkspaceContext(selectedSalesMapContext),
    [selectedSalesMapContext],
  );

  const { data: mapStyles, error: mapStylesError, isLoading: mapStylesLoading } = useSWR<SalesMapStyleRecord[]>(
    tenantId && mappingAccess.accessEnabled && salesAccessEnabled
      ? ['sales-map-system-style', tenantId, salesWorkspaceContext?.catalog_owner_tenant_id ?? '', salesWorkspaceContext?.entitlement_id ?? '']
      : null,
    () => listMapStyles({ limit: 200, share_scope: 'public' }, salesWorkspaceContext) as Promise<SalesMapStyleRecord[]>,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      errorRetryCount: 0,
      dedupingInterval: 15000,
    },
  );

  const salesStyle = useMemo(
    () => (mapStyles ?? []).find(isSalesMapDefaultStyle) ?? null,
    [mapStyles],
  );
  const [routeOverlayGeoJSON, setRouteOverlayGeoJSON] = useState<Record<string, unknown> | null>(null);
  const [hoveredZone, setHoveredZone] = useState<SalesServiceabilityZoneOverlaySelection | null>(null);
  const [selectedZone, setSelectedZone] = useState<SalesServiceabilityZoneOverlaySelection | null>(null);

  const { data: serviceabilityZones } = useSWR(
    salesWorkspaceContext?.catalog_owner_tenant_id
      ? [
          'sales-serviceability-zones',
          salesWorkspaceContext.catalog_owner_tenant_id,
          salesWorkspaceContext.entitlement_id ?? '',
        ]
      : null,
    () => fetchSalesServiceabilityZones(salesWorkspaceContext),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      errorRetryCount: 0,
      dedupingInterval: 15000,
    },
  );

  const { data: salesStyleDetail } = useSWR<SalesMapStyleRecord>(
    salesStyle?.id
      ? ['sales-map-style-detail', salesStyle.id, salesWorkspaceContext?.catalog_owner_tenant_id ?? '', salesWorkspaceContext?.entitlement_id ?? '']
      : null,
    () => getMapStyle(salesStyle!.id, salesWorkspaceContext) as Promise<SalesMapStyleRecord>,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      errorRetryCount: 0,
      dedupingInterval: 15000,
    },
  );

  const renderRuntimeSurfaceSection = useCallback(
    composeRuntimeSurfaceSectionRenderers(
      createRuntimeSurfaceComponentRenderer({
        [SALES_MAP_ORDERS_COMPONENT_ID]: args => renderSalesMapOrdersSection(args),
        [SALES_MAP_DETAILS_COMPONENT_ID]: args => renderSalesMapDetailsSection(args),
        [SALES_MAP_ROUTING_QUOTE_COMPONENT_ID]: args => renderSalesMapRoutingQuoteSection(args),
        [SALES_MAP_COLLABORATION_COMPONENT_ID]: args =>
          renderSalesMapCollaborationSection(args, salesWorkspaceContext),
      }),
    ),
    [salesWorkspaceContext],
  );

  const zoneOverlayGeoJSON = useMemo(
    () => buildSalesMapZoneOverlayGeoJSON({ zones: serviceabilityZones ?? null, selectedZoneID: selectedZone?.zone_id ?? null }),
    [selectedZone?.zone_id, serviceabilityZones],
  );
  const runtimeOverlayGeoJSON = useMemo(
    () => mergeRuntimeOverlayGeoJSON([zoneOverlayGeoJSON, routeOverlayGeoJSON]),
    [routeOverlayGeoJSON, zoneOverlayGeoJSON],
  );

  useEffect(() => {
    setHoveredZone(null);
    setSelectedZone(null);
    setRouteOverlayGeoJSON(null);
  }, [salesWorkspaceContext?.catalog_owner_tenant_id, salesWorkspaceContext?.entitlement_id]);

  useEffect(() => {
    logSalesMapPageDebug('runtime overlay state changed', {
      salesStyleId: salesStyle?.id ?? null,
      salesStyleDetailId: salesStyleDetail?.id ?? null,
      overlay: summarizeRuntimeOverlayGeoJSON(runtimeOverlayGeoJSON),
    });
  }, [runtimeOverlayGeoJSON, salesStyle?.id, salesStyleDetail?.id]);

  const handleRuntimeOverlayFeatureHover = useCallback(
    (feature: { featureId: string | number | null; geometryType: string | null; properties: Record<string, unknown> } | null) => {
      setHoveredZone(readSalesServiceabilityZoneSelection(feature));
    },
    [],
  );

  const handleRuntimeOverlayFeatureClick = useCallback(
    (feature: { featureId: string | number | null; geometryType: string | null; properties: Record<string, unknown> }) => {
      const next = readSalesServiceabilityZoneSelection(feature);
      if (!next) {
        return;
      }
      setSelectedZone(current => (current?.zone_id === next.zone_id ? null : next));
    },
    [],
  );

  const isRuntimeOverlayFeatureInteractive = useCallback(
    (feature: { properties: Record<string, unknown> }) => feature.properties.overlay_kind === 'serviceability_zone',
    [],
  );

  if (!tenantId) {
    return (
      <MappingFeatureUnavailable
        title="Sales map unavailable"
        description="Switch into a tenant workspace to load the tenant sales map."
      />
    );
  }

  if (!mappingAccess.mappingFeatureEnabled) {
    return <MappingFeatureUnavailable />;
  }

  if (!mappingAccess.mappingPermissionGranted) {
    return <MappingPermissionDenied />;
  }

  if (!salesAccessEnabled) {
    return (
      <MappingFeatureUnavailable
        title="Sales map unavailable"
        description="This tenant has not enabled the sales runtime."
      />
    );
  }

  if (mapStylesLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3">
        <svg className="h-5 w-5 animate-spin text-muted-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span className="text-sm text-muted-foreground">Loading sales map…</span>
      </div>
    );
  }

  if (mapStylesError) {
    return (
      <MappingFeatureUnavailable
        title="Sales map unavailable"
        description={mapStylesError instanceof Error ? mapStylesError.message : 'Failed to load the platform sales map.'}
      />
    );
  }

  if (!salesStyle?.id) {
    return (
      <MappingFeatureUnavailable
        title="Sales map unavailable"
        description="The platform sales map experience is not available in this environment."
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-border/60 bg-background/90 px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <label className="hidden shrink-0 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground sm:block" htmlFor="sales-map-context">
            View as
          </label>
          <Select
            value={selectedSalesMapContext?.id ?? ''}
            onValueChange={value => {
              setSalesMapContextSelectionTouched(true);
              setSelectedSalesMapContextId(value || null);
            }}
          >
            <SelectTrigger id="sales-map-context" className="h-8 min-w-0 flex-1 text-sm" />
            <SelectContent>
              {salesMapContextOptions.map(option => (
                <SelectItem key={option.id} value={option.id}>
                  {option.label}{option.kind === 'linked-seller' ? ' — linked seller' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="shrink-0">
            {selectedSalesMapContext?.request_enabled ? (
              <Badge variant="success">Requests on</Badge>
            ) : (
              <Badge variant="muted">View only</Badge>
            )}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <SalesMapRoutingProvider
          salesContext={salesWorkspaceContext}
          requestEnabled={selectedSalesMapContext?.request_enabled ?? false}
          onRuntimeOverlayGeoJSONChange={setRouteOverlayGeoJSON}
        >
          <div className="relative h-full min-h-0">
            <MapStyleWorkbenchPage
              styleId={salesStyle.id}
              modeOverride="view"
              mobileRightPaneDefaultOpen={false}
              viewerHideIdleRightPane
              hideViewModeToolbar
              runtimeOverlayGeoJSON={runtimeOverlayGeoJSON}
              runtimeOverlayStyle={SALES_MAP_RUNTIME_OVERLAY_STYLE}
              runtimeOverlayInteractive
              isRuntimeOverlayFeatureInteractive={isRuntimeOverlayFeatureInteractive}
              onRuntimeOverlayFeatureHover={handleRuntimeOverlayFeatureHover}
              onRuntimeOverlayFeatureClick={handleRuntimeOverlayFeatureClick}
              onPinnedFeatureChange={feature => {
                if (feature) {
                  setSelectedZone(null);
                }
              }}
              renderRuntimeSurfaceSection={renderRuntimeSurfaceSection}
              runtimeSurfacePanelProps={{
                headerMode: 'compact',
                hideDescription: true,
                hideSummary: true,
                hideInspectAction: true,
              }}
              hydrateRuntimeFeatureState={args => hydrateSalesMapRuntimeFeatureState(args, salesWorkspaceContext)}
              mappingQueryContext={salesWorkspaceContext}
            />
            {hoveredZone && !selectedZone ? (
              <div className="pointer-events-none absolute bottom-4 left-4 z-20 hidden md:block">
                <SalesMapServiceabilityZoneHoverCard zone={hoveredZone} />
              </div>
            ) : null}
            {selectedZone ? (
              <div className="absolute inset-x-0 bottom-0 top-auto z-30 h-[60%] border-t border-border bg-background md:inset-x-auto md:inset-y-0 md:right-0 md:h-auto md:w-[360px] md:border-l md:border-t-0">
                <SalesMapServiceabilityZonePanel zone={selectedZone} onClose={() => setSelectedZone(null)} />
              </div>
            ) : null}
          </div>
        </SalesMapRoutingProvider>
      </div>
    </div>
  );
}

export default SalesMapPage;
